import React, { useEffect, useState } from "react";
import { useLoaderData, useLocation, useNavigate } from "react-router-dom";
import LocalStorageAPI from "../api/localStorageApi";
import BackButton from "./BackButton";
import { Link, LinkPage } from "../types/Link";
import ParameterTable from "./ParameterTable";
import EditButton from "./EditButton";

interface ParamsObject {
  params: {
    pageId: string;
  };
}

export const loader: (
  input: ParamsObject
) => Promise<LinkPage | null> = async ({ params }) => {
  if (!params.pageId) {
    return null;
  }
  return LocalStorageAPI.getLinkPageById(params.pageId);
};

function extractVars(s: string): string[] {
  const vars = [];
  const regex = /{{(.*?)}}/g;
  let match;
  while ((match = regex.exec(s)) !== null) {
    vars.push(match[1]);
  }
  return vars;
}

function buildRegex(replacements: Record<string, string>): RegExp {
  return new RegExp(
    Object.keys(replacements)
      .map((key) => "{{" + key + "}}")
      .map((key) => key.replace(/[.*+\-?^${}()|[\]\\]/g, "\\$&"))
      .join("|"),
    "g"
  );
}

function substituteURLVariables(
  url: string,
  replacements: Record<string, string>
): string {
  const regex = buildRegex(replacements);
  return url.replace(regex, (match) => {
    let strippedMatch = match.slice(2, -2);
    return replacements[strippedMatch] ?? match;
  });
}

// Helper function to generate the highlighted URL
function renderHighlightedURL(
  url: string,
  replacements: Record<string, string>
): JSX.Element[] {
  if (!Object.keys(replacements).length) {
    return [<span key={0}>{url}</span>];
  }
  const regex = new RegExp(
    Object.keys(replacements)
      .map((key) => "{{" + key + "}}")
      .map((key) => key.replace(/[.*+\-?^${}()|[\]\\]/g, "\\$&"))
      .join("|"),
    "g"
  );
  const parts: JSX.Element[] = [];

  let lastIndex = 0;
  url.replace(regex, (match, index) => {
    if (index > lastIndex) {
      parts.push(<span key={parts.length}>{url.slice(lastIndex, index)}</span>);
    }
    let strippedMatch = match.slice(2, -2);
    parts.push(
      <span
        key={parts.length}
        className="text-blue-600 border-dotted border-2 border-blue-600 rounded"
        style={{}}
      >
        {replacements[strippedMatch] ?? match}
      </span>
    );
    lastIndex = index + match.length;
    return match;
  });

  if (lastIndex < url.length) {
    parts.push(<span key={parts.length}>{url.slice(lastIndex)}</span>);
  }

  return parts;
}

function getQueryParamsObj(): Record<string, string> {
  const searchParams = new URLSearchParams(window.location.search);

  const queryParamsObj: Record<string, string> = {};
  searchParams.forEach((value, key) => {
    queryParamsObj[key] = value;
  });
  return queryParamsObj;
}

function bracePad(s: string): string {
  return "{{" + s + "}}";
}

const LinkPageList: React.FC = () => {
  const [queryParams, setQueryParams] = useState(getQueryParamsObj());
  const linkPage = useLoaderData() as LinkPage;
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    let queryParamsObj = getQueryParamsObj();
    for (const link of linkPage.Links) {
      for (const varName of extractVars(link.URL)) {
        if (queryParamsObj[varName] === undefined) {
          queryParamsObj[varName] = bracePad(varName);
        }
      }
    }
    for (const key in linkPage.DefaultVariableValues) {
      if (!linkPage.DefaultVariableValues.hasOwnProperty(key)) {
        continue;
      }
      if (!linkPage.DefaultVariableValues[key]) {
        continue;
      }
      if (
        queryParamsObj[key] === undefined ||
        queryParamsObj[key] === bracePad(key)
      ) {
        queryParamsObj[key] = linkPage.DefaultVariableValues[key];
      }
    }

    setQueryParams(queryParamsObj);
  }, [location.search, linkPage.Links, linkPage.DefaultVariableValues]);

  if (!linkPage) {
    return <div>Link page not found</div>;
  }

  const handleEdit = (updatedData: Record<string, string>) => {
    setQueryParams(updatedData);

    // Update the URL with the modified query parameters
    const searchParams = new URLSearchParams();
    for (const [key, value] of Object.entries(updatedData)) {
      searchParams.set(key, value);
    }

    navigate(
      {
        pathname: location.pathname,
        search: searchParams.toString(),
      },
      { replace: true }
    );
  };

  return (
    <div>
      <div className="columns-2 flex flex-row justify-between">
        <BackButton />
        <EditButton to={"./edit"} />
      </div>
      <h2 className="text-2xl font-semibold mb-2">{linkPage.Name}</h2>
      <p className="text-gray-700 mb-4">{linkPage.Description}</p>
      <ParameterTable data={queryParams} onEdit={handleEdit}></ParameterTable>
      <ul className="space-y-0">
        {linkPage.Links.map((link: Link) => (
          <li key={link.Name} className="border-b border-gray-300">
            <a
              href={substituteURLVariables(link.URL, queryParams)}
              className="block py-4 px-2 w-full text-gray-700 hover:bg-gray-200"
            >
              <span className="text-m font-semibold">{link.Name}</span>
              <br />
              <span className="text-s text-gray-500">
                {renderHighlightedURL(link.URL, queryParams)}
              </span>
            </a>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default LinkPageList;
